import { BackIcon } from "@/components/icons/back-icon";
import { TextButton } from "@/components/ui/buttons";
import { SettingModalHeader } from "@/features/user-profile/components/setting-modal-header";
import { cn } from "@/utils/cn";
import { IonContent, IonPage, useIonRouter } from "@ionic/react";
import type { HelpFaqItem } from "../../constants/help-faq";
import { helpFaq } from "../../constants/help-faq";
import { useOpenNewCoachChat } from "../../hooks/use-open-coach-chat";
import { useOpenHelpChat } from "../../hooks/use-open-help-chat";

interface Props {
  /** When provided the component renders without IonPage/decorations (embedded inside the chat modal). */
  onBack?: () => void;
  /** When true, forces the full IonPage render even when onBack is provided (use inside IonNav). */
  standalone?: boolean;
  /** When provided, overrides the default pill tap handler (used by the embedded chat context). */
  onSelectItem?: (item: HelpFaqItem) => void;
  /**
   * Overrides "Ask me something else..." (used by the embedded chat, where the
   * chat is already open). Defaults to opening a brand-new coach chat.
   */
  onAskSomethingElse?: () => void;
}

export default function HelpScreen({
  onBack,
  standalone,
  onSelectItem,
  onAskSomethingElse,
}: Props = {}) {
  const router = useIonRouter();
  const openHelpChat = useOpenHelpChat();
  const openNewCoachChat = useOpenNewCoachChat();
  const embedded = !!onBack && !standalone;

  const handleBack = onBack ?? (() => router.goBack());
  const handleSelectItem = onSelectItem ?? openHelpChat;
  const handleAskSomethingElse = onAskSomethingElse ?? openNewCoachChat;

  const pillBase =
    "relative flex h-[48px] shrink-0 items-center overflow-hidden rounded-[32px] px-4";

  const scrollContent = (
    <div className="flex min-h-full flex-col items-center justify-center px-8 pt-8 pb-28">
      <div className="flex w-full max-w-[324px] flex-col items-center gap-4">
        {helpFaq.map((item) => (
          <button
            key={item.question}
            type="button"
            onClick={() => handleSelectItem(item)}
            className={cn(
              pillBase,
              "border border-[#ebe0fb] drop-shadow-[0px_8px_32.35px_rgba(64,0,255,0.1)]",
            )}
          >
            <div aria-hidden className="absolute inset-0 rounded-[32px] bg-white" />
            <span className="text-os-brand relative text-[14px] font-medium whitespace-nowrap">
              {item.question}
            </span>
            <div
              aria-hidden
              className="absolute inset-0 rounded-[32px] shadow-[inset_0px_-12px_12px_0px_rgba(98,10,213,0.06)]"
            />
          </button>
        ))}
        <button
          type="button"
          onClick={handleAskSomethingElse}
          className={cn(pillBase, "border border-[#620ad5]")}
        >
          <div aria-hidden className="absolute inset-0 rounded-[32px] bg-[#620ad5]" />
          <span className="relative text-[14px] font-medium whitespace-nowrap text-white">
            Ask me something else...
          </span>
          <div
            aria-hidden
            className="absolute inset-0 rounded-[32px] shadow-[inset_0px_-12px_12px_0px_rgba(0,0,0,0.06)]"
          />
        </button>
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className="relative z-10 flex h-full flex-col">
        <div
          className={cn(
            "relative flex items-center justify-between px-2 pb-3",
            "pt-[calc(var(--safe-area-inset-top)+0.5rem)]",
          )}
        >
          <TextButton
            onClick={handleBack}
            aria-label="Back"
            className="flex h-11 w-11 items-center justify-center"
          >
            <BackIcon className="stroke-[#620ad5]" />
          </TextButton>
          <h1 className="pointer-events-none text-center text-[17px] font-bold text-black">
            Help &amp; Support
          </h1>
          <div aria-hidden className="h-11 w-11" />
        </div>
        <div className="flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {scrollContent}
        </div>
      </div>
    );
  }

  return (
    <IonPage>
      <div className="relative z-10 flex h-full flex-col" style={{ backgroundColor: "#fcfbfb" }}>
        <SettingModalHeader title="Help & Support" onBack={handleBack} />
        <IonContent style={{ "--background": "#fcfbfb" } as React.CSSProperties}>
          {scrollContent}
        </IonContent>
      </div>
    </IonPage>
  );
}
